import { Button } from "@material-ui/core";
import AssignProductDialog from "@saleor/components/AssignProductDialog";
import { DEFAULT_INITIAL_SEARCH_DATA, PAGINATE_BY } from "@saleor/config";
import useBulkActions from "@saleor/hooks/useBulkActions";
import useNavigator from "@saleor/hooks/useNavigator";
import useNotifier from "@saleor/hooks/useNotifier";
import { createPaginationState } from "@saleor/hooks/usePaginator";
import { maybe } from "@saleor/misc";
import { orderUrl } from "@saleor/orders/urls";
import { productUrl } from "@saleor/products/urls";
import useProductSearch from "@saleor/searches/useProductSearch";
import { ReorderEvent } from "@saleor/types";
import createDialogActionHandlers from "@saleor/utils/handlers/dialogActionHandlers";
import { move } from "@saleor/utils/lists";
import { mapEdgesToItems } from "@saleor/utils/maps";
import React from "react";
import { FormattedMessage, useIntl } from "react-intl";

import ComboDeleteDialg from "../components/ComboDeleteDialog";
import ComboDetails, {
  ComboDetailsPageTab,
  ComboFormData
} from "../components/ComboDetails/ComboDetails";
import {
  useComboAddProductsMutation,
  useComboDeleteMutation,
  useComboRemoveProductsMutation,
  useComboReorderProductsMutation,
  useComboUpdateMutation
} from "../mutations";
import { useComboByIdQuery } from "../queries";
import {
  comboListPath,
  comboListUrl,
  comboUrl,
  ComboUrlDialog,
  ComboUrlQueryParams
} from "../urls";

interface ComboDetailsProps {
  id: string;
  params: ComboUrlQueryParams;
}
const Combo: React.FC<ComboDetailsProps> = ({ id, params }) => {
  const { data, loading, refetch } = useComboByIdQuery({
    variables: {
      id
    }
  });
  const navigate = useNavigator();
  const intl = useIntl();
  const notify = useNotifier();
  const paginationState = createPaginationState(PAGINATE_BY, params);
  const { isSelected, listElements, reset, toggle, toggleAll } = useBulkActions(
    params.ids
  );
  const [openModal, closeModal] = createDialogActionHandlers<
    ComboUrlDialog,
    ComboUrlQueryParams
  >(navigate, params => comboUrl(id, params), params);
  const {
    loadMore: loadMoreProducts,
    search: searchProducts,
    result: searchProductsOpts
  } = useProductSearch({
    variables: DEFAULT_INITIAL_SEARCH_DATA
  });
  const [comboUpdate, comboUpdateOpts] = useComboUpdateMutation({
    onCompleted: data => {
      if (data.comboUpdate.errors.length === 0) {
        notify({
          status: "success",
          text: intl.formatMessage({
            defaultMessage: "General Information Update Success"
          })
        });
        refetch();
      }
    }
  });
  const [comboDelete, comboDeleteOpts] = useComboDeleteMutation({
    onCompleted: data => {
      if (data.comboDelete.errors.length === 0) {
        notify({
          status: "success",
          text: intl.formatMessage({
            defaultMessage: "combo delete"
          })
        });
        navigate(comboListUrl());
      }
    }
  });
  const [
    comboAssginProducts,
    comboAssginProductsOpts
  ] = useComboAddProductsMutation({
    onCompleted: data => {
      if (data.comboAddProducts.errors.length === 0) {
        notify({
          status: "success",
          text: intl.formatMessage({
            defaultMessage: "Combo Assgin Products Success"
          })
        });
        closeModal();
        refetch();
      }
    }
  });
  const [comboUnAssginProducts] = useComboRemoveProductsMutation({
    onCompleted: data => {
      if (data.comboRemoveProducts.errors.length === 0) {
        notify({
          status: "success",
          text: intl.formatMessage({
            defaultMessage: "Combo UnAssgin Products Success"
          })
        });
        refetch();
      }
    }
  });
  const [comboReorderProducts] = useComboReorderProductsMutation({
    onCompleted: data => {
      if (data.comboReorderProducts.errors.length === 0) {
        notify({
          status: "success",
          text: intl.formatMessage({
            defaultMessage: "Combo Reorder Products Success"
          })
        });
        refetch();
      }
    }
  });
  const handleProductReorder = ({ newIndex, oldIndex }: ReorderEvent) =>
    comboReorderProducts({
      optimisticResponse: {
        comboReorderProducts: {
          __typename: "ComboReorderProducts",
          combo: {
            ...data.comboById,
            comboProducts: move(
              data.comboById.comboProducts[oldIndex],
              data.comboById.comboProducts,
              (a, b) => a.id === b.id,
              newIndex
            )
          },
          errors: []
        }
      },
      variables: {
        comboId: id,
        move: {
          comboProductId: data.comboById.comboProducts[oldIndex].id,
          sortOrder: newIndex - oldIndex
        }
      }
    });
  const handleSubmit = async (data: ComboFormData) => {
    const result = await comboUpdate({
      variables: {
        id,
        input: {
          name: data.name,
          totalPrice: data.totalPrice,
          labelText: data.labelText,
          labelLink: data.labelLink,
          enable: data.enable
        }
      }
    });
    return result.data.comboUpdate.errors;
  };
  const changeTab = (tab: ComboDetailsPageTab) => {
    reset();
    navigate(comboUrl(id, { activeTab: tab }));
  };
  const handleProductUnassign = (ids: string[]) =>
    comboUnAssginProducts({
      variables: {
        ...paginationState,
        id,
        productIds: ids
      }
    });

  return (
    <>
      <ComboDetails
        combo={data?.comboById}
        onProductReorder={handleProductReorder}
        disabled={loading}
        onNextPage={null}
        onPreviousPage={null}
        toggle={toggle}
        toggleAll={toggleAll}
        isChecked={isSelected}
        selected={listElements.length}
        onSubmit={handleSubmit}
        onTabClick={changeTab}
        activeTab={params.activeTab}
        onBack={() => navigate(comboListPath)}
        saveButtonBarState={comboUpdateOpts.status}
        onDelete={() => openModal("remove")}
        variantListToolbar={
          <Button
            color="primary"
            onClick={() =>
              openModal("unassign-product", {
                ids: listElements
              })
            }
          >
            <FormattedMessage
              defaultMessage="Unassign"
              description="unassign product sku from specialOffer, button"
              id="specialOfferDetailsUnassignProduct"
            />
          </Button>
        }
        onProductAssign={() => openModal("assign-product")}
        onProductUnassign={variantId => handleProductUnassign([variantId])}
        onProductClick={id => () => navigate(productUrl(id))}
        redirectOrder={id => () => navigate(orderUrl(id))}
      />
      <AssignProductDialog
        confirmButtonState={comboAssginProductsOpts.status}
        hasMore={searchProductsOpts.data?.search?.pageInfo.hasNextPage}
        open={params.action === "assign-product"}
        onFetch={searchProducts}
        onFetchMore={loadMoreProducts}
        loading={searchProductsOpts.loading}
        onClose={closeModal}
        onSubmit={products =>
          comboAssginProducts({
            variables: {
              ...paginationState,
              id,
              productIds: products.map(product => product.id)
            }
          })
        }
        products={mapEdgesToItems(searchProductsOpts?.data?.search)?.filter(
          suggestedProduct => suggestedProduct.id
        )}
      />
      <ComboDeleteDialg
        open={params.action === "remove"}
        title={maybe(() => data.comboById.name, "……")}
        confirmButtonState={comboDeleteOpts.status}
        onClose={closeModal}
        onConfirm={() =>
          comboDelete({
            variables: {
              id
            }
          })
        }
      />
    </>
  );
};
export default Combo;
